/**
 * AgentInboxTriagePage - the unknown-sender triage queue (SPEC §9,
 * Phase 8b).
 *
 * Lists `inbound_emails` rows with `parsed_action='pending_review'`
 * by default; an agent (or system_admin) can promote a row into a
 * real ticket or discard it. Filter toggles between Pending /
 * Discarded / All. The two write actions are guarded server-side
 * with 409 Conflict; we surface that as a non-blocking banner and
 * refetch the list when it happens.
 *
 * The page intentionally renders body_preview as plain text inside a
 * <pre> block. The backend truncates `body_text` (not body_html), so
 * there is no HTML to sanitize here.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Alert, Button, Card, Select, Spinner, Textarea } from "@/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  useAdminInboxCount,
  useAdminInboxList,
  useDiscardInboxRow,
  useMarkInboxRowAsSpam,
  usePromoteInboxRow,
} from "@/hooks/useAdminInbox";
import { useAuthStore } from "@/stores/auth";
import type { InboxRowResponse, InboxStatusFilter } from "@/types/api";

const FILTER_STORAGE_KEY = "buyist-support:inbox-filter";

const FILTER_OPTIONS: { value: InboxStatusFilter; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "discarded", label: "Discarded" },
  { value: "all", label: "All" },
];

function readPersistedFilter(): InboxStatusFilter {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (raw === "pending" || raw === "discarded" || raw === "all") {
      return raw;
    }
  } catch {
    /* noop */
  }
  return "pending";
}

function writePersistedFilter(filter: InboxStatusFilter): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, filter);
  } catch {
    /* noop */
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

function reviewerLabel(row: InboxRowResponse): string {
  // Prefer the joined full_name; fall back to email, then to a short
  // UUID, then to a placeholder. The UUID case only fires when the
  // backend joined a now-deleted user row, which the ON DELETE SET
  // NULL FK turns into reviewed_by_user_id staying populated while
  // the joined name+email come back null - unlikely in dev but
  // possible after a real admin tear-down.
  if (row.reviewed_by_name && row.reviewed_by_name.trim().length > 0) {
    return row.reviewed_by_name;
  }
  if (row.reviewed_by_email && row.reviewed_by_email.trim().length > 0) {
    return row.reviewed_by_email;
  }
  if (row.reviewed_by_user_id) {
    return row.reviewed_by_user_id.slice(0, 8);
  }
  return "(agent)";
}

function extractStatus(err: unknown): number | null {
  if (typeof err === "object" && err !== null) {
    const response = (err as { response?: { status?: number } }).response;
    if (typeof response?.status === "number") return response.status;
  }
  return null;
}

function extractDetailMessage(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null) {
    const response = (err as { response?: { data?: { detail?: unknown } } })
      .response;
    const detail = response?.data?.detail;
    if (typeof detail === "string" && detail.length > 0) return detail;
  }
  if (err instanceof Error && err.message.length > 0) return err.message;
  return fallback;
}

type ConfirmMode = "discard" | "mark_spam" | null;

interface InboxRowViewProps {
  row: InboxRowResponse;
  expanded: boolean;
  onToggle: () => void;
  onPromote: (row: InboxRowResponse) => void;
  onDiscard: (row: InboxRowResponse, reason: string | null) => void;
  onMarkSpam: (row: InboxRowResponse, reason: string | null) => void;
  isMutating: boolean;
}

function InboxRowView({
  row,
  expanded,
  onToggle,
  onPromote,
  onDiscard,
  onMarkSpam,
  isMutating,
}: InboxRowViewProps): JSX.Element {
  const [confirming, setConfirming] = useState<ConfirmMode>(null);
  const [reason, setReason] = useState("");

  const isPending = row.parsed_action === "pending_review";
  const fromLine = row.from_name
    ? `${row.from_name} <${row.from_email}>`
    : row.from_email;

  const startConfirm = (mode: ConfirmMode) => {
    setConfirming(mode);
    setReason("");
  };
  const cancelConfirm = () => {
    setConfirming(null);
    setReason("");
  };

  const submitConfirm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = reason.trim();
    const cleaned = trimmed.length > 0 ? trimmed : null;
    if (confirming === "discard") {
      onDiscard(row, cleaned);
    } else if (confirming === "mark_spam") {
      onMarkSpam(row, cleaned);
    }
  };

  const confirmLabel =
    confirming === "mark_spam" ? "Confirm: mark spam" : "Confirm discard";
  const confirmTestId =
    confirming === "mark_spam"
      ? `inbox-mark-spam-confirm-${row.id}`
      : `inbox-discard-confirm-${row.id}`;
  const reasonHelp =
    confirming === "mark_spam"
      ? "Reason (optional). Marking spam blocks this sender from creating future tickets."
      : "Reason (optional)";

  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer hover:bg-neutral-50"
        data-testid={`inbox-row-${row.id}`}
      >
        <td className="px-3 py-3 text-sm text-neutral-800">{fromLine}</td>
        <td className="px-3 py-3 text-sm text-neutral-700">
          <span className="line-clamp-2">
            {row.subject ?? "(no subject)"}
          </span>
        </td>
        <td className="px-3 py-3 text-xs text-neutral-500">
          {formatRelative(row.received_at)}
        </td>
        <td className="px-3 py-3 text-right text-xs text-neutral-500">
          <i
            className={`fa-duotone fa-thin ${
              expanded ? "fa-chevron-up" : "fa-chevron-down"
            } text-base text-neutral-400`}
            aria-hidden="true"
          />
        </td>
      </tr>
      {expanded && (
        <tr className="bg-neutral-50">
          <td colSpan={4} className="px-3 py-4">
            <div className="space-y-3">
              <div className="text-xs text-neutral-500">
                <div>
                  <span className="font-semibold text-neutral-600">From:</span>{" "}
                  {fromLine}
                </div>
                <div>
                  <span className="font-semibold text-neutral-600">To:</span>{" "}
                  {row.to_email}
                </div>
                <div>
                  <span className="font-semibold text-neutral-600">
                    Message-ID:
                  </span>{" "}
                  <span className="font-mono">{row.message_id}</span>
                </div>
                <div>
                  <span className="font-semibold text-neutral-600">
                    Received:
                  </span>{" "}
                  {new Date(row.received_at).toLocaleString()}
                </div>
              </div>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded border border-neutral-200 bg-white p-3 text-xs text-neutral-700">
                {row.body_preview ?? "(no body)"}
              </pre>
              {isPending && confirming === null && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => onPromote(row)}
                    isLoading={isMutating}
                    data-testid={`inbox-promote-${row.id}`}
                  >
                    Create ticket
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => startConfirm("discard")}
                    disabled={isMutating}
                    data-testid={`inbox-discard-${row.id}`}
                  >
                    Discard
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => startConfirm("mark_spam")}
                    disabled={isMutating}
                    className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                    data-testid={`inbox-mark-spam-${row.id}`}
                  >
                    <i
                      className="fa-duotone fa-thin fa-shield-xmark mr-1.5"
                      aria-hidden="true"
                    />
                    Mark as Spam
                  </Button>
                </div>
              )}
              {isPending && confirming !== null && (
                <form
                  onSubmit={submitConfirm}
                  className="space-y-2 rounded border border-neutral-200 bg-white p-3"
                  data-testid={`inbox-confirm-form-${row.id}`}
                  data-confirm-mode={confirming}
                >
                  <Textarea
                    id={`inbox-confirm-reason-${row.id}`}
                    label={reasonHelp}
                    rows={2}
                    maxLength={2000}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={cancelConfirm}
                      disabled={isMutating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 active:bg-red-800"
                      isLoading={isMutating}
                      data-testid={confirmTestId}
                    >
                      {confirmLabel}
                    </Button>
                  </div>
                </form>
              )}
              {!isPending && (
                <div className="text-xs text-neutral-500">
                  {row.parsed_action === "discarded" && (
                    <>
                      Discarded{" "}
                      {row.reviewed_at
                        ? formatRelative(row.reviewed_at)
                        : "(unknown time)"}{" "}
                      by {reviewerLabel(row)}. Reason:{" "}
                      {row.parse_error ?? "(no reason given)"}
                    </>
                  )}
                  {row.parsed_action === "marked_spam" && (
                    <>
                      <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
                        SPAM
                      </span>{" "}
                      Marked as spam{" "}
                      {row.reviewed_at
                        ? formatRelative(row.reviewed_at)
                        : "(unknown time)"}{" "}
                      by {reviewerLabel(row)}. Sender is now on the block
                      list. Reason: {row.parse_error ?? "(no reason given)"}
                    </>
                  )}
                  {row.parsed_action !== "discarded" &&
                    row.parsed_action !== "marked_spam" && (
                      <>Already acted: {row.parsed_action ?? "unknown"}.</>
                    )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function AgentInboxTriagePage(): JSX.Element {
  usePageTitle("Inbox - Buyist Support");

  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);

  const [filter, setFilter] = useState<InboxStatusFilter>(() =>
    readPersistedFilter(),
  );
  useEffect(() => {
    writePersistedFilter(filter);
  }, [filter]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const list = useAdminInboxList(filter);
  const counts = useAdminInboxCount();
  const promote = usePromoteInboxRow();
  const discard = useDiscardInboxRow();
  const markSpam = useMarkInboxRowAsSpam();

  const subtitle = useMemo(() => {
    if (!counts.data) return "Loading inbox stats...";
    return `${counts.data.pending} unknown senders awaiting review, ${counts.data.discarded} discarded, ${counts.data.marked_spam} marked as spam`;
  }, [counts.data]);

  // Customers cannot see this page. The route already lives behind
  // RequireAuth and the nav item is agentsOnly, but a customer who
  // pastes /inbox into the URL bar should bounce home rather than
  // wait on a 401 from the API call.
  if (role === "customer") {
    return <Navigate to="/" replace />;
  }

  function toggleExpand(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleError(err: unknown, fallback: string): void {
    const status = extractStatus(err);
    if (status === 409) {
      setConflictMessage(
        "Another agent already acted on this row. The list will refresh.",
      );
      setActionError(null);
      void list.refetch();
      void counts.refetch();
      return;
    }
    setConflictMessage(null);
    setActionError(extractDetailMessage(err, fallback));
  }

  async function handlePromote(row: InboxRowResponse): Promise<void> {
    setConflictMessage(null);
    setActionError(null);
    try {
      const result = await promote.mutateAsync(row.id);
      navigate(`/tickets/${result.public_code.toLowerCase()}`);
    } catch (err) {
      handleError(err, "Failed to promote this row.");
    }
  }

  async function handleDiscard(
    row: InboxRowResponse,
    reason: string | null,
  ): Promise<void> {
    setConflictMessage(null);
    setActionError(null);
    try {
      await discard.mutateAsync({ id: row.id, reason });
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (err) {
      handleError(err, "Failed to discard this row.");
    }
  }

  async function handleMarkSpam(
    row: InboxRowResponse,
    reason: string | null,
  ): Promise<void> {
    setConflictMessage(null);
    setActionError(null);
    try {
      await markSpam.mutateAsync({ id: row.id, reason });
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (err) {
      handleError(err, "Failed to mark this row as spam.");
    }
  }

  const rows = list.data ?? [];
  const isMutating =
    promote.isPending || discard.isPending || markSpam.isPending;

  return (
    <div className="mx-auto w-full max-w-6xl">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <i
            className="fa-duotone fa-thin fa-inbox text-3xl text-primary-600"
            aria-hidden="true"
          />
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Inbox</h1>
            <p
              className="mt-1 text-sm text-neutral-500"
              data-testid="inbox-subtitle"
            >
              {subtitle}
            </p>
          </div>
        </div>
        <div className="w-full sm:w-56">
          <Select
            id="inbox-status-filter"
            aria-label="Inbox filter"
            options={FILTER_OPTIONS}
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value as InboxStatusFilter);
              setExpanded(new Set());
            }}
          />
        </div>
      </header>

      {conflictMessage && (
        <Alert
          variant="warning"
          title="Row already acted on"
          className="mb-4"
        >
          {conflictMessage}
        </Alert>
      )}

      {actionError && (
        <Alert
          variant="error"
          title="Could not complete action"
          className="mb-4"
        >
          {actionError}
        </Alert>
      )}

      {list.isError && (
        <Alert variant="error" title="Failed to load inbox" className="mb-4">
          {list.error instanceof Error
            ? list.error.message
            : "Unknown error."}
        </Alert>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  From
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Subject
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Received
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {list.isLoading && (
                <tr>
                  <td colSpan={4} className="px-3 py-12 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!list.isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-12 text-center"
                    data-testid="inbox-empty"
                  >
                    <i
                      className="fa-duotone fa-thin fa-inbox text-4xl text-neutral-300"
                      aria-hidden="true"
                    />
                    <p className="mt-2 text-sm font-medium text-neutral-700">
                      Inbox is empty.
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      All incoming email has been triaged.
                    </p>
                  </td>
                </tr>
              )}
              {!list.isLoading &&
                rows.map((row) => (
                  <InboxRowView
                    key={row.id}
                    row={row}
                    expanded={expanded.has(row.id)}
                    onToggle={() => toggleExpand(row.id)}
                    onPromote={(r) => void handlePromote(r)}
                    onDiscard={(r, reason) => void handleDiscard(r, reason)}
                    onMarkSpam={(r, reason) => void handleMarkSpam(r, reason)}
                    isMutating={isMutating}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default AgentInboxTriagePage;
